import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { MonitorPlay, Trash2, HardDrive, CalendarX2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Loader, { EmptyState } from '@/components/Loader';
import Pagination from '@/components/Pagination';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import ScreenshotLightbox from '@/components/ScreenshotLightbox';
import ScreenshotTile from '@/components/ScreenshotTile';
import { trackerService, userService } from '@/services';

const LIMIT = 60;

function formatBytes(bytes = 0) {
  if (!bytes) return '0 MB';
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

export default function TrackerScreenshots() {
  const qc = useQueryClient();
  // from/to rather than a single date — the API has always accepted a range on
  // this endpoint, the page just never exposed it. Defaults to today..today, so
  // the landing view is the same day it always was.
  const [filters, setFilters] = useState({
    user: '',
    from: dayjs().format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
    minActivity: '',
    maxActivity: '',
  });
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [range, setRange] = useState({
    from: dayjs().startOf('month').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  });
  const [busy, setBusy] = useState(false);

  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tracker', 'screenshots', filters, page],
    queryFn: () => trackerService.adminScreenshots({ ...filters, page, limit: LIMIT }),
  });

  const items = data?.items || [];
  const storage = data?.storage;
  const employeeName = filters.user
    ? (users.data?.items || []).find((u) => u._id === filters.user)?.name || 'this employee'
    : 'all employees';

  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const activityFilterActive = filters.minActivity !== '' || filters.maxActivity !== '';

  // Delete every screenshot in the filtered date range for the selected employee
  // (or all). Note: deletion is by date — the activity-% filter narrows the view,
  // not the delete.
  const sameDay = filters.from === filters.to;
  const rangeLabel = sameDay
    ? dayjs(filters.from).format('DD MMM YYYY')
    : `${dayjs(filters.from).format('DD MMM YYYY')} – ${dayjs(filters.to).format('DD MMM YYYY')}`;

  const deleteFiltered = () => {
    setConfirmState({
      title: 'Delete screenshots',
      message: `Delete all screenshots for ${employeeName} ${sameDay ? 'on' : 'between'} ${rangeLabel}?${
        activityFilterActive
          ? ` The activity filter only narrows what you see — the whole ${sameDay ? 'day' : 'range'} is deleted.`
          : ''
      } The files are permanently removed.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          const r = await trackerService.bulkDeleteScreenshots({
            user: filters.user || undefined,
            from: filters.from,
            to: filters.to,
          });
          toast.success(`Deleted ${r.deleted} screenshot(s) · freed ${formatBytes(r.freedBytes)}`);
          qc.invalidateQueries({ queryKey: ['tracker'] });
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed to delete');
        }
      },
    });
  };

  // Delete every screenshot in a date range for the selected employee (or all).
  const deleteRange = async () => {
    setBusy(true);
    try {
      const r = await trackerService.bulkDeleteScreenshots({
        user: filters.user || undefined,
        from: range.from,
        to: range.to,
      });
      toast.success(`Deleted ${r.deleted} screenshot(s) · freed ${formatBytes(r.freedBytes)}`);
      setShowDeleteAll(false);
      qc.invalidateQueries({ queryKey: ['tracker'] });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const removeShot = (shot) => {
    setConfirmState({
      title: 'Delete screenshot',
      message: 'This screenshot will be permanently removed from the server. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await trackerService.removeScreenshot(shot._id);
          toast.success('Screenshot deleted');
          setLightbox(null);
          qc.invalidateQueries({ queryKey: ['tracker'] });
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed');
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Screenshots"
        subtitle="Every screenshot the desktop tracker captured"
        actions={
          <>
            {storage && (
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                title={`${storage.totalCount} screenshots stored on the server`}
              >
                <HardDrive size={14} /> {formatBytes(storage.totalBytes)}
                <span className="text-slate-400">· {storage.totalCount} shots</span>
              </span>
            )}
            <Link to="/admin/tracker" className="btn-secondary">
              <MonitorPlay size={14} /> Live tracker
            </Link>
          </>
        }
      />

      <Card>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3 mb-5">
          <div>
            <select aria-label="Employee" className="input w-52" value={filters.user} onChange={(e) => setFilter({ user: e.target.value })}>
              <option value="">All employees</option>
              {(users.data?.items || []).map((u) => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>
          {/* From / to, joined into one control. No labels — the two date fields
              side by side already read as a range, and the page has no room for
              a caption on every filter. */}
          <div className="inline-flex items-stretch rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden focus-within:ring-2 focus-within:ring-brand-500">
            <input
              type="date"
              aria-label="From date"
              className="w-40 bg-transparent px-3 py-2 text-sm focus:outline-none"
              value={filters.from}
              max={filters.to}
              onChange={(e) => setFilter({ from: e.target.value })}
            />
            <span aria-hidden className="w-px bg-slate-300 dark:bg-slate-700" />
            <input
              type="date"
              aria-label="To date"
              className="w-40 bg-transparent px-3 py-2 text-sm focus:outline-none"
              value={filters.to}
              min={filters.from}
              onChange={(e) => setFilter({ to: e.target.value })}
            />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              {/* Activity presets as a joined segmented control */}
              <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                {[
                  { label: '< 10%', min: '', max: 10 },
                  { label: '< 25%', min: '', max: 25 },
                  { label: '< 50%', min: '', max: 50 },
                  { label: '> 60%', min: 60, max: '' },
                ].map((p, i) => {
                  const active =
                    String(filters.minActivity) === String(p.min) && String(filters.maxActivity) === String(p.max);
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setFilter({ minActivity: p.min, maxActivity: p.max })}
                      className={`px-2.5 py-2 text-xs font-medium whitespace-nowrap transition ${
                        i > 0 ? 'border-l border-slate-200 dark:border-slate-700' : ''
                      } ${
                        active
                          ? 'bg-brand-500 text-white'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {(filters.minActivity !== '' || filters.maxActivity !== '') && (
                <button
                  type="button"
                  onClick={() => setFilter({ minActivity: '', maxActivity: '' })}
                  className="text-xs text-rose-600 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              className="btn-danger px-2.5 py-2"
              onClick={deleteFiltered}
              disabled={items.length === 0}
              title={`Delete screenshots for ${employeeName} · ${rangeLabel}`}
              aria-label={`Delete screenshots for ${employeeName} · ${rangeLabel}`}
            >
              <CalendarX2 size={16} />
            </button>
            <button
              className="btn-secondary px-2.5 py-2"
              onClick={() => setShowDeleteAll(true)}
              title="Delete all screenshots by date range"
              aria-label="Delete all screenshots by date range"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {isError ? (
          <EmptyState
            title="Could not load screenshots"
            subtitle={error?.response?.data?.message || error?.message || 'Something went wrong'}
          />
        ) : isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <EmptyState
            title="No screenshots found"
            subtitle={activityFilterActive ? 'No shots match this activity range — try widening it or clearing the filter.' : 'Try a different employee or date range.'}
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {items.map((shot, i) => (
              <ScreenshotTile
                key={shot._id}
                shot={shot}
                showUser
                onClick={() => setLightbox({ items, index: i })}
              />
            ))}
          </div>
        )}

        <Pagination page={page} limit={LIMIT} total={data?.total || 0} onChange={setPage} />
      </Card>

      <ScreenshotLightbox
        state={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        onDelete={removeShot}
      />
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />

      {showDeleteAll && (
        <Modal
          open
          onClose={() => setShowDeleteAll(false)}
          title="Delete screenshots by date range"
          footer={
            <>
              <button className="btn-secondary" onClick={() => setShowDeleteAll(false)} disabled={busy}>Cancel</button>
              <button className="btn-danger" onClick={deleteRange} disabled={busy}>
                <Trash2 size={14} /> {busy ? 'Deleting…' : 'Delete range'}
              </button>
            </>
          }
        >
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            This permanently deletes all screenshots for{' '}
            <span className="font-semibold">{employeeName}</span> between the chosen dates. This cannot be undone.
          </p>
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="label">From</label>
              <input
                type="date"
                className="input w-44"
                value={range.from}
                max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">To</label>
              <input
                type="date"
                className="input w-44"
                value={range.to}
                min={range.from}
                max={dayjs().format('YYYY-MM-DD')}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Employee: <span className="font-medium">{employeeName}</span> · pick a specific employee in the filter above to limit the deletion.
          </div>
        </Modal>
      )}
    </div>
  );
}
